import {
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { IDLength } from '../../../../utils/settings';
import { useEffect } from 'react';
import { calculateAgesAndFlags } from '../../../Utils/AgeCalc';
import FileUploadWrapper from '../../../Utils/FileUpload';

const PersonDetailsForm = ({
  person,
  setPerson,
  relations = [],
  handleUpload,
  onPanBlur,
  confirmation_text,
  panLoader,
  errors = {},
  showData = false,
  isEdit = false,
  panError,
  panInputRef = null,
}) => {
  const handleChange = (field, value) => {
    if (field === 'dob') {
      const { age } = calculateAgesAndFlags([value])[0];
      setPerson('dob', value);
      setPerson('age', age);
    } else {
      setPerson(field, value);
    }
  };
  useEffect(() => {
    if (relations?.length) {
      const isCurrentRelationValid = relations.some(
        (rel) => rel.value === person.relation,
      );
      if (!isCurrentRelationValid) {
        handleChange('relation', relations[0].value);
      }
    }
  }, [relations]);
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Typography
          variant='body2'
          sx={{ fontSize: '13px', mb: 1, color: '#636363', fontWeight: 'bold' }}
        >
          Relation*
        </Typography>
        <FormControl
          fullWidth
          disabled={relations.length <= 1}
        >
          <Select
            value={person.relation}
            onChange={(e) => handleChange('relation', e.target.value)}
            error={!!errors.relation}
            size='small'
          >
            {relations.map((rel) => (
              <MenuItem
                key={rel.value}
                value={rel.value}
              >
                {rel.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ color: '#CC0000' }}>
            {errors.relation}
          </FormHelperText>
        </FormControl>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Typography
          variant='body2'
          sx={{ fontSize: '13px', mb: 1, color: '#636363', fontWeight: 'bold' }}
        >
          PAN*
        </Typography>
        <TextField
          fullWidth
          size='small'
          value={person.id_number}
          onChange={(e) =>
            handleChange('id_number', e.target.value.toUpperCase())
          }
          onBlur={(e) => onPanBlur?.(e.target.value.toUpperCase())}
          sx={{
            '& input': {
              textTransform: 'uppercase',
            },
          }}
          error={!!panError || !!errors.id_number}
          helperText={panError || errors.id_number}
          inputProps={{
            maxLength: IDLength.PAN,
          }}
          InputProps={{
            endAdornment: panLoader ? (
              <InputAdornment position='end'>
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          disabled={isEdit}
          inputRef={panInputRef}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Box
          display='flex'
          gap={2}
        >
          <Box
            flex={1}
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
          >
            <FileUploadWrapper
              title='Upload image of PAN'
              acceptedFileTypes={[
                'image/png',
                'image/jpeg',
                'image/jpg',
                'image/heic',
              ]}
              handleUpload={handleUpload}
              existingFileName={isEdit ? person.file_name : null}
              existingFileUrl={isEdit ? person.id_str_base64 : null}
            />
          </Box>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'end' }}
      >
        {person.id_number?.length === 10 && panLoader ? (
          <Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '13px',
                mb: 1,
                color: '#636363',
                fontWeight: 'bold',
              }}
            >
              Loading...
            </Typography>
          </Box>
        ) : person.id_number?.length === 10 && showData ? (
          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', alignItems: 'baseline', p: 1 }}
          >
            <Grid
              item
              xs={6}
              sm={6}
            >
              <Typography
                variant='body2'
                sx={{
                  fontSize: '13px',
                  mb: 1,
                  color: '#666666',
                  fontWeight: 'bold',
                }}
              >
                Name
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 1, color: '#333333' }}
              >
                {person?.full_name || 'NA'}
              </Typography>
              <FormHelperText sx={{ color: '#CC0000' }}>
                {errors.full_name}
              </FormHelperText>
            </Grid>

            <Grid
              item
              xs={6}
              sm={6}
            >
              <Typography
                variant='body2'
                sx={{
                  fontSize: '13px',
                  mb: 1,
                  color: '#666666',
                  fontWeight: 'bold',
                }}
              >
                Date of Birth*
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 1, color: '#333333' }}
              >
                {person?.dob || 'NA'}
              </Typography>
              <FormHelperText sx={{ color: '#CC0000' }}>
                {errors.dob}
              </FormHelperText>
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
            >
              <Typography
                variant='body2'
                sx={{
                  fontSize: '13px',
                  mb: 1,
                  color: '#666666',
                  fontWeight: 'bold',
                }}
              >
                Gender*
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 1, color: '#333333' }}
              >
                {person?.gender || 'NA'}
              </Typography>
              <FormHelperText sx={{ color: '#CC0000' }}>
                {errors.gender}
              </FormHelperText>
            </Grid>

            <Grid
              item
              xs={6}
              sm={6}
            >
              <Typography
                variant='body2'
                sx={{
                  fontSize: '13px',
                  mb: 1,
                  color: '#666666',
                  fontWeight: 'bold',
                }}
              >
                Age
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 1, color: errors.age ? '#CC0000' : '#333333' }}
              >
                {person?.age ? `${person?.age} Y` : 'NA'}
              </Typography>
              <FormHelperText sx={{ color: '#CC0000' }}>
                {errors.age}
              </FormHelperText>
            </Grid>
          </Grid>
        ) : null}
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Typography
          variant='body2'
          sx={{ fontSize: '13px', mb: 1, color: '#636363', fontWeight: 'bold' }}
        >
          Phone Number*
        </Typography>
        <TextField
          fullWidth
          size='small'
          value={person.phone_number}
          error={!!errors.phone_number}
          helperText={errors.phone_number}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value) && value.length <= 10) {
              handleChange('phone_number', value);
            }
          }}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <FormControlLabel
          sx={{ alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={person.confirm || false}
              onChange={(e) => handleChange('confirm', e.target.checked)}
              sx={{ mr: 1, py: 0 }}
            />
          }
          label={confirmation_text?.label}
        />
        {errors.confirm && (
          <FormHelperText sx={{ color: '#CC0000' }}>
            {errors.confirm}
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  );
};

export default PersonDetailsForm;
